import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, TrendingDown, TrendingUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useSettings } from "@/context/SettingsContext";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import { TYRES } from "@/lib/format";
import { STATUS_CLS, analyzeStint, buildTips, findTarget, fmtP, gainModels, nextColdFromSession, stintsOf, tyreLabel } from "@/lib/pressureTargets";

const TIP_ICON = { ok: CheckCircle2, high: TrendingUp, low: TrendingDown, warn: AlertTriangle, info: Info };
const TIP_CLS = { ok: "text-[#10B981]", high: "text-[#FF3B30]", low: "text-[#007AFF]", warn: "text-[#FFB800]", info: "text-slate-300" };

export const StatusChip = ({ status, testid }) => {
  const { t } = useT();
  return <span data-testid={testid} className={`inline-block font-mono text-[10px] uppercase px-1.5 py-0.5 border rounded-sm ${STATUS_CLS[status]}`}>{t(`p_${status}`)}</span>;
};

export const Tips = ({ tips, testid }) => (
  <ul data-testid={testid} className="space-y-1.5">
    {tips.map((tip, i) => {
      const I = TIP_ICON[tip.kind];
      return <li key={i} className={`flex gap-2 text-sm ${TIP_CLS[tip.kind]}`}><I className="w-4 h-4 mt-0.5 shrink-0" /><span>{tip.text}</span></li>;
    })}
  </ul>
);

const WheelCard = ({ p, w, next, unit, n }) => {
  const { t } = useT();
  return (
    <div data-testid={`pa${n}-wheel-${p}`} className="bg-[#090A0F] border border-[#282D3D] rounded-sm p-3 space-y-1.5">
      <div className="flex items-center justify-between"><span className="font-mono text-[10px] uppercase text-slate-400">{t(`tyre_${p}`)}</span><StatusChip status={w.status} testid={`pa${n}-status-${p}`} /></div>
      <div className="font-mono text-xs flex justify-between"><span className="text-slate-500">{t("pressure_hot")}</span><span className="text-white">{fmtP(w.hot, unit)}</span></div>
      <div className="font-mono text-xs flex justify-between"><span className="text-slate-500">{t("pa_delta")}</span><span data-testid={`pa${n}-delta-${p}`} className={STATUS_CLS[w.status].split(" ")[0]}>{fmtP(w.delta, unit, true)}</span></div>
      <div className="font-mono text-xs flex justify-between"><span className="text-slate-500">{t("pa_gain")}</span><span className="text-[#FFB800]">{fmtP(w.gain, unit, true)}</span></div>
      <div className="font-mono text-xs flex justify-between border-t border-[#282D3D] pt-1.5"><span className="text-slate-400">{t("pa_cold_next")}</span>
        <span data-testid={`pa${n}-next-${p}`} className="text-[#10B981] font-bold">{fmtP(next[p], unit)} <span className="text-slate-500 font-normal">({fmtP(next[p] != null && w.cold != null ? next[p] - w.cold : null, unit, true)})</span></span>
      </div>
    </div>
  );
};

function useHistory(track) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (track) api.get("/analysis/pressures", { params: { track } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [track]);
  return rows;
}

const StintAnalysis = ({ st, history, form }) => {
  const { t } = useT();
  const targets = useSettings().settings.tyre_targets;
  const unit = form.pressure_unit;
  const cur = form.conditions?.track_temp;
  const [nextTemp, setNextTemp] = useState(cur ?? "");
  useEffect(() => { setNextTemp(cur ?? ""); }, [cur]);
  const target = findTarget(targets, st.brand, st.model);
  const a = analyzeStint(st, target);
  const same = useMemo(() => history.flatMap(stintsOf).filter((h) => h.key !== st.key && tyreLabel(h.brand, h.model).toLowerCase() === tyreLabel(st.brand, st.model).toLowerCase()), [history, st.key, st.brand, st.model]);
  const models = useMemo(() => gainModels(same), [same]);
  const next = nextColdFromSession(a, models, cur, nextTemp);
  const slopes = TYRES.map((p) => models[p]?.slope).filter((s) => s != null);
  const tips = buildTips(a, t, unit, next, nextTemp, cur);
  return (
    <div data-testid={`pressure-analysis-${st.n}`} className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-heading uppercase font-bold text-lg">{form.pit ? `${t(`stint${st.n}`)} · ` : ""}{tyreLabel(st.brand, st.model) || t("tyre_unknown")}</span>
        {target && <span className="font-mono text-xs text-slate-400">{t("tyre_target")} {fmtP(target.target, unit)} ±{fmtP(target.tolerance, unit)} {unit}</span>}
        <StatusChip status={a.overall} testid={`pa${st.n}-overall`} />
        <label className="ml-auto flex items-center gap-2 font-mono text-[10px] uppercase text-slate-400">{t("pa_exp_temp")}
          <Input data-testid={`pa${st.n}-next-temp`} type="number" value={nextTemp} onChange={(e) => setNextTemp(e.target.value)} className="w-20 h-9 bg-[#090A0F] border-[#282D3D] font-mono text-sm rounded-sm" />
        </label>
      </div>
      {target && <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">{TYRES.map((p) => <WheelCard key={p} p={p} w={a.wheels[p]} next={next} unit={unit} n={st.n} />)}</div>}
      <div className="bg-[#090A0F] border border-[#282D3D] rounded-sm p-3 space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#10B981]">{t("pa_next")}</div>
        <Tips tips={tips} testid={`pa${st.n}-tips`} />
        <p data-testid={`pa${st.n}-history-note`} className="font-mono text-[10px] text-slate-500">
          {slopes.length ? t("pa_based").replace("{n}", same.length).replace("{s}", `${fmtP(slopes.reduce((x, y) => x + y, 0) / slopes.length, unit, true)} ${unit}`) : t("pa_no_hist")}
        </p>
      </div>
    </div>
  );
};

export const PressureAnalysisPanel = ({ form, track, sessionId }) => {
  const { t } = useT();
  const history = useHistory(track);
  return (
    <section data-testid="pressure-analysis-panel" className="bg-[#12141C] border border-[#10B981]/30 rounded-sm p-4 space-y-6">
      <h3 className="font-heading uppercase font-bold text-xl flex items-center gap-2"><span className="w-1 h-4 bg-[#10B981]" />{t("pa_title")}</h3>
      {stintsOf({ ...form, id: sessionId || "current" }).map((st) => <StintAnalysis key={st.n} st={st} history={history} form={form} />)}
    </section>
  );
};
